import React, { useEffect, useState } from 'react';
import { Tablet, Wifi, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface KioskPairingScreenProps {
  pairingCode: string | null;
  expiresAt: Date | null;
  error: string | null;
  isLoading: boolean;
  onRequestNewCode: () => void;
}

/**
 * Full-screen pairing display for kiosk devices
 * Shows a 6-digit code that expires after 10 minutes
 */
export function KioskPairingScreen({
  pairingCode,
  expiresAt,
  error,
  isLoading,
  onRequestNewCode,
}: KioskPairingScreenProps) {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  // Request code on mount
  useEffect(() => {
    if (!pairingCode && !isLoading && !error) {
      onRequestNewCode();
    }
  }, [pairingCode, isLoading, error, onRequestNewCode]);

  // Update countdown
  useEffect(() => {
    if (!expiresAt) return;

    const updateRemaining = () => {
      const remaining = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
      setTimeRemaining(remaining);

      if (remaining === 0) {
        // Code expired, request new one
        onRequestNewCode();
      }
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, onRequestNewCode]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
      <div className="max-w-lg w-full text-center space-y-8">
        {/* Logo/Icon */}
        <div className="flex justify-center">
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-xl">
            <Tablet className="w-12 h-12 text-white" />
          </div>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-white">Kiosk Setup</h1>
          <p className="text-slate-400 text-lg">
            Enter this code in your TagInn dashboard to connect this kiosk
          </p>
        </div>

        {/* Pairing Code */}
        {pairingCode && !error ? (
          <div className="space-y-4">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700">
              <div className="text-7xl font-mono font-bold text-white tracking-[0.5em] pl-[0.5em]">
                {pairingCode}
              </div>
            </div>

            {/* Countdown */}
            {timeRemaining !== null && (
              <div className="flex items-center justify-center gap-2 text-slate-400">
                <Wifi className="w-4 h-4 animate-pulse" />
                <span>
                  Code expires in{' '}
                  <span className="font-mono text-white">{formatTime(timeRemaining)}</span>
                </span>
              </div>
            )}
          </div>
        ) : error ? (
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8">
              <p className="text-red-400 text-lg">{error}</p>
            </div>
            <Button
              onClick={onRequestNewCode}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        ) : (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700">
            <div className="flex items-center justify-center gap-3">
              <RefreshCw className="w-6 h-6 text-green-400 animate-spin" />
              <span className="text-slate-300 text-xl">Generating code...</span>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="space-y-3 text-left bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h3 className="text-white font-medium">How to connect:</h3>
          <ol className="space-y-2 text-slate-400">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center justify-center">1</span>
              <span>Open your TagInn dashboard</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center justify-center">2</span>
              <span>Go to Settings &rarr; Employee Shifts</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center justify-center">3</span>
              <span>Click "Pair New Kiosk" and enter the code above</span>
            </li>
          </ol>
        </div>

        {/* Footer */}
        <p className="text-slate-500 text-sm">
          This device will stay connected until you revoke it from your dashboard
        </p>
      </div>
    </div>
  );
}
